import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Flame, Shield, Leaf, Crown, Heart, Star, Check, ChevronRight, ArrowLeft, Lock, Unlock } from 'lucide-react';
import { features, products, usageSteps } from '../data/mock';

const GOLD = '#C9A84C';
const iconMap = { Zap, Flame, Shield, Leaf, Crown, Heart, Star };
const PROD_IMG =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_chocolate-supreme-mx/artifacts/cc7a20ae3afe26b7_Gemini_Generated_Image_xowbhtxowbhtxowb.jpg';

export default function Features() {
  const [activeFeature, setActiveFeature] = useState(null);
  const [revealedFormulas, setRevealedFormulas] = useState(new Set());

  const toggleFormula = (id) => {
    setRevealedFormulas((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <main style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Montserrat, sans-serif' }}>
      {/* PAGE HERO */}
      <section
        style={{
          position: 'relative',
          paddingTop: '10rem',
          paddingBottom: '5rem',
          padding: '10rem 1.5rem 5rem',
          background: '#0A0A0A',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.06) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: '48rem', margin: '0 auto', textAlign: 'center' }}>
          <p
            style={{ color: GOLD, fontSize: '0.68rem', letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '1rem' }}
          >
            Aura King &amp; Queen
          </p>
          <h1
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 'clamp(2.8rem, 5vw, 4.5rem)',
              fontWeight: 300,
              color: '#fff',
              marginBottom: '1.5rem',
              lineHeight: 1.1,
            }}
          >
            Características{' '}
            <span style={{ color: GOLD, fontStyle: 'italic' }}>Premium</span>
          </h1>
          <p
            style={{
              fontSize: '0.85rem',
              lineHeight: 1.85,
              color: 'rgba(255,255,255,0.5)',
              maxWidth: '34rem',
              margin: '0 auto',
            }}
          >
            Descubre los beneficios, ingredientes y la ciencia detrás del chocolate afrodisíaco más exclusivo de
            México.
          </p>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section style={{ padding: '5rem 1.5rem', background: '#070707' }}>
        <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <p style={{ color: GOLD, fontSize: '0.68rem', letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              Beneficios
            </p>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                fontWeight: 300,
                color: '#fff',
              }}
            >
              Seis Razones para{' '}
              <span style={{ color: GOLD, fontStyle: 'italic' }}>Elegir Aura</span>
            </h2>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            style={{ gap: '1.25rem' }}
          >
            {features.map((f) => {
              const Icon = iconMap[f.icon] || Star;
              const isActive = activeFeature === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => setActiveFeature(isActive ? null : f.id)}
                  style={{
                    background: isActive ? 'rgba(201,168,76,0.05)' : '#111111',
                    border: `1px solid ${isActive ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.12)'}`,
                    padding: '2rem',
                    cursor: 'pointer',
                    transition: 'border-color 0.3s ease, background 0.3s ease, transform 0.3s ease',
                    transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = 'rgba(201,168,76,0.12)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div
                      style={{
                        width: '3rem',
                        height: '3rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: 'rgba(201,168,76,0.1)',
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={20} style={{ color: GOLD }} />
                    </div>
                    <h3 style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>{f.title}</h3>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', lineHeight: 1.75, marginBottom: '0.75rem' }}>
                    {f.description}
                  </p>
                  {isActive && (
                    <p
                      style={{
                        color: 'rgba(201,168,76,0.82)',
                        fontSize: '0.75rem',
                        lineHeight: 1.75,
                        borderTop: '1px solid rgba(201,168,76,0.2)',
                        paddingTop: '0.75rem',
                        marginTop: '0.75rem',
                      }}
                    >
                      {f.detail}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '1rem', color: GOLD, fontSize: '0.68rem', letterSpacing: '0.1em' }}>
                    <span>{isActive ? 'Menos info' : 'Más info'}</span>
                    <ChevronRight
                      size={12}
                      style={{ transition: 'transform 0.2s ease', transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INGREDIENTS COMPARISON */}
      <section style={{ padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <p style={{ color: GOLD, fontSize: '0.68rem', letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              Ingredientes
            </p>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                fontWeight: 300,
                color: '#fff',
              }}
            >
              Fórmula{' '}
              <span style={{ color: GOLD, fontStyle: 'italic' }}>King vs Queen</span>
            </h2>
          </div>
          <div
            className="grid grid-cols-1 lg:grid-cols-2"
            style={{ gap: '1.5rem' }}
          >
            {products.map((p) => (
              <div
                key={p.id}
                style={{ background: '#111111', border: '1px solid rgba(201,168,76,0.15)', padding: '2rem' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '2rem',
                    paddingBottom: '1.25rem',
                    borderBottom: '1px solid rgba(201,168,76,0.12)',
                  }}
                >
                  <Crown size={17} style={{ color: GOLD }} />
                  <h3
                    style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: '1.75rem',
                      fontWeight: 300,
                      color: '#fff',
                      flex: 1,
                    }}
                  >
                    {p.name}
                  </h3>
                  <span
                    style={{
                      padding: '0.2rem 0.75rem',
                      border: '1px solid rgba(201,168,76,0.25)',
                      background: 'rgba(201,168,76,0.07)',
                      color: GOLD,
                      fontSize: '0.62rem',
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {p.badge}
                  </span>
                </div>
                {/* Benefits */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {p.benefits.map((b, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <Check size={12} style={{ color: GOLD, flexShrink: 0 }} />
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>{b}</span>
                    </li>
                  ))}
                </ul>

                {/* Fórmula Confidencial */}
                <div
                  style={{
                    borderTop: '1px solid rgba(201,168,76,0.12)',
                    paddingTop: '1.25rem',
                  }}
                >
                  {!revealedFormulas.has(p.id) ? (
                    /* Locked state */
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1.5rem',
                        border: '1px dashed rgba(201,168,76,0.2)',
                        background: 'rgba(201,168,76,0.03)',
                        textAlign: 'center',
                      }}
                    >
                      <Lock size={20} style={{ color: GOLD, opacity: 0.6 }} />
                      <div>
                        <p style={{ color: GOLD, fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>
                          Fórmula Confidencial
                        </p>
                        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', margin: 0 }}>
                          Los ingredientes activos son información exclusiva
                        </p>
                      </div>
                      <button
                        onClick={() => toggleFormula(p.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.55rem 1.25rem',
                          background: GOLD,
                          color: '#000',
                          fontSize: '0.62rem',
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'opacity 0.3s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.82')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      >
                        <Unlock size={12} />
                        Revelar Fórmula
                      </button>
                    </div>
                  ) : (
                    /* Revealed state */
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Unlock size={14} style={{ color: GOLD }} />
                          <span style={{ color: GOLD, fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase' }}>
                            Ingredientes Activos
                          </span>
                        </div>
                        <button
                          onClick={() => toggleFormula(p.id)}
                          style={{
                            background: 'none',
                            border: '1px solid rgba(201,168,76,0.25)',
                            color: 'rgba(201,168,76,0.5)',
                            fontSize: '0.6rem',
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            padding: '0.3rem 0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            transition: 'opacity 0.3s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.6')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                          <Lock size={10} />
                          Ocultar
                        </button>
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {p.ingredients.map((ing, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span
                              style={{
                                width: '1.4rem',
                                height: '1.4rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: 'rgba(201,168,76,0.1)',
                                color: GOLD,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {i + 1}
                            </span>
                            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>{ing}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO USE */}
      <section style={{ padding: '6rem 1.5rem', background: '#060606' }}>
        <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <p style={{ color: GOLD, fontSize: '0.68rem', letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              Instrucciones
            </p>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                fontWeight: 300,
                color: '#fff',
              }}
            >
              Cómo{' '}
              <span style={{ color: GOLD, fontStyle: 'italic' }}>Usarlo</span>
            </h2>
          </div>
          <div
            className="grid grid-cols-2 lg:grid-cols-4"
            style={{ gap: '2.5rem' }}
          >
            {usageSteps.map((step, i) => (
              <div
                key={i}
                style={{ textAlign: 'center' }}
                onMouseEnter={(e) => {
                  e.currentTarget.querySelector('.step-num').style.opacity = '0.45';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.querySelector('.step-num').style.opacity = '0.18';
                }}
              >
                <div
                  className="step-num"
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: '4rem',
                    fontWeight: 300,
                    color: GOLD,
                    opacity: 0.18,
                    marginBottom: '0.75rem',
                    lineHeight: 1,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  {step.step}
                </div>
                <div style={{ width: '2.5rem', height: '1px', background: GOLD, margin: '0 auto 1.25rem' }} />
                <h3 style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.75rem' }}>{step.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', lineHeight: 1.7 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT SPECS */}
      <section style={{ padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                fontWeight: 300,
                color: '#fff',
              }}
            >
              Información del{' '}
              <span style={{ color: GOLD, fontStyle: 'italic' }}>Producto</span>
            </h2>
          </div>

          {/* Two-column: image left + specs right */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              border: '1px solid rgba(201,168,76,0.15)',
              overflow: 'hidden',
            }}
          >
            {/* Image */}
            <div style={{ position: 'relative', minHeight: '380px', background: '#0d0d0d' }}>
              <img
                src={PROD_IMG}
                alt="Aura King & Queen"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  position: 'absolute',
                  inset: 0,
                }}
              />
            </div>

            {/* Specs table */}
            <div style={{ background: '#111111' }}>
              {[
                ['Presentación', '12 sachets por bolsa'],
                ['Contenido por sachet', '20g'],
                ['Contenido neto', '120g'],
                ['Uso recomendado', '1 sachet por día, mayores de 18 años'],
                ['Variantes', 'King (Para Él) · Queen (Para Ella)'],
                ['Distribución', 'República Mexicana'],
                ['Almacenamiento', 'Lugar fresco y seco, protegido de la luz y evitar calor'],
                ['Conservantes', 'Sin conservadores artificiales'],
              ].map(([label, value], i, arr) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem 2rem',
                    borderBottom: i < arr.length - 1 ? '1px solid rgba(201,168,76,0.08)' : 'none',
                  }}
                >
                  <span
                    style={{
                      width: '12rem',
                      flexShrink: 0,
                      color: 'rgba(201,168,76,0.7)',
                      fontSize: '0.68rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          padding: '5rem 1.5rem',
          textAlign: 'center',
          background: '#060606',
          borderTop: '1px solid rgba(201,168,76,0.1)',
        }}
      >
        <div style={{ maxWidth: '36rem', margin: '0 auto' }}>
          <p style={{ color: GOLD, fontSize: '0.68rem', letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '1rem' }}>
            Listo para Vivir la Experiencia
          </p>
          <h2
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              fontWeight: 300,
              color: '#fff',
              marginBottom: '2rem',
            }}
          >
            Pide tu Aura{' '}
            <span style={{ color: GOLD, fontStyle: 'italic' }}>Ahora</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem' }}>
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1.1rem 2.5rem',
                border: '1px solid rgba(201,168,76,0.35)',
                color: GOLD,
                fontSize: '0.72rem',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                transition: 'background 0.3s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <ArrowLeft size={13} />
              Volver al Inicio
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
